// Mirrored from mju-ai-workbench/shared/schemas/compute.ts at feat/d443-compute (b97aa183) — keep the two identical but for this line and the import suffix.
/**
 * The `compute` clause of `.mojoup/policy.json` (roadmap D443/D444, docs/plans/2026-10-clusters-and-agentic-pipelines.md):
 * where a node may run on Kubernetes and how the agent sessions on it are held. Workbench-owned: AIOE copies this file
 * for its console (provenance runs the other way), and an organisation's console renders the clause from the policy
 * manifest like any other, whose field descriptions live in src/platform/policyManifest.ts (`FIELD_DESCRIPTIONS`).
 *
 *  - `kubernetes`: whether a person may deploy nodes into a cluster from the Compute view, and into which
 *    namespaces; a managed policy without the clause leaves it to the person (nothing is forced). `allowed: false`
 *    refuses; `namespaces` (an explicit list) confines the deploy; `requireNetworkPolicy` refuses a cluster whose
 *    network plugin does not enforce NetworkPolicy, since agent pods are isolated by it.
 *  - `clusters`: the clusters the organisation approved, filled by AIOE. Each names its node pools: the namespace a
 *    pool lives in (left out: the cluster's first namespace, else `default`) and the tag a member's `runsOn` uses to be placed there. A workbench lists them in the Teams
 *    placement picker beside the person's own clusters.
 *  - `runtimeClass`: per data classification, the RuntimeClass (gVisor, Kata) an agent pod must run under. A
 *    classification with no entry runs under the cluster's default runtime.
 *  - `sessions`: the most agent pods one node pool runs at once (`maxPerPool`) and the minutes of silence after which
 *    an agent pod is stopped (`idleMinutes`).
 *
 * Plain zod, no Node.
 */
import { z } from "zod";
import { classificationSchema } from "./projects.js";
/** A DNS-1123 label: what a namespace, a pool or a RuntimeClass is called. */
const dnsLabel = z.string().min(1).max(63).regex(/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/, "a lower-case name: letters, digits and dashes");
const dnsSubdomain = z.string().min(1).max(253).regex(/^[a-z0-9]([-.a-z0-9]*[a-z0-9])?$/, "a lower-case name: letters, digits, dashes and dots");
export const computeKubernetesPolicySchema = z.object({
    allowed: z.boolean().optional(),
    namespaces: z.array(dnsLabel).max(50).optional(),
    requireNetworkPolicy: z.boolean().optional(),
});
export const computePoolSchema = z.object({
    /** The pool's name: what a person picks and a member's `runsOn` tag is built from. */
    name: dnsLabel,
    /** Left out: the cluster's first namespace, else `default`. */
    namespace: dnsLabel.optional(),
    /** The node tag the pool's nodes carry (the placement tag). Left out: `pool:<name>`. */
    tag: z.string().min(1).max(100).optional(),
});
export const computeClusterSchema = z.object({
    id: z.string().min(1).max(100),
    name: z.string().min(1).max(100),
    pools: z.array(computePoolSchema).max(50).optional(),
});
export const computeSessionsSchema = z.object({
    maxPerPool: z.number().int().min(1).max(1000).optional(),
    idleMinutes: z.number().int().min(1).max(10080).optional(),
});
export const computePolicySchema = z.object({
    kubernetes: computeKubernetesPolicySchema.optional(),
    clusters: z.array(computeClusterSchema).max(50).optional(),
    runtimeClass: z.partialRecord(classificationSchema, dnsSubdomain).optional(),
    sessions: computeSessionsSchema.optional(),
});
/** What a pool allows when the policy says nothing: generous, since a person's own cluster is theirs. */
export const DEFAULT_SESSION_LIMITS = { maxPerPool: 8, idleMinutes: 30 };
/** The RuntimeClass an agent pod of this classification must run under, or undefined for the cluster's default. */
export function runtimeClassFor(policy, classification) {
    if (!classification)
        return undefined;
    return policy?.runtimeClass?.[classification];
}
/** The limits in force: the policy's, then the defaults. */
export function sessionLimits(policy) {
    return {
        maxPerPool: policy?.sessions?.maxPerPool ?? DEFAULT_SESSION_LIMITS.maxPerPool,
        idleMinutes: policy?.sessions?.idleMinutes ?? DEFAULT_SESSION_LIMITS.idleMinutes,
    };
}
/** Why a deploy into `namespace` is refused by the clause, in a sentence; undefined when it is allowed. */
export function computeRefusal(policy, namespace) {
    const k = policy?.kubernetes;
    if (k?.allowed === false)
        return "Your organisation does not allow nodes to be deployed into a cluster.";
    if (k?.namespaces && !k.namespaces.includes(namespace)) {
        return k.namespaces.length ? `Your organisation allows nodes only in ${k.namespaces.join(", ")}.` : "Your organisation names no namespace a node may be deployed into.";
    }
    return undefined;
}
/** The tag a member's `runsOn` uses to be placed on a pool. */
export function poolTag(pool) {
    return pool.tag ?? `pool:${pool.name}`;
}
//# sourceMappingURL=compute.js.map